/**
 * Widget chat flottant "Assistant SESAM AI"
 * Bouton flottant bas-droite → ouvre panneau chat multi-turn.
 * Restreint aux admins (guard côté React + guard côté modules_allowed sinon).
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Sparkles, X, Send, Loader2, Trash2, Bot, User as UserIcon } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Convertit un markdown simple (**gras**, - liste, retours ligne) en HTML sûr
function renderMarkdown(text) {
  if (!text) return '';
  const escape = (s) => s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escape(text)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code class="bg-slate-100 px-1 rounded text-[12px]">$1</code>')
    .replace(/^\s*[-*]\s+(.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>)/gs, '<ul class="list-disc pl-5 space-y-0.5">$1</ul>')
    .replace(/\n/g, '<br/>');
}

export default function AIAssistantWidget({ user }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => {
    const stored = localStorage.getItem('sesam_ai_session');
    if (stored) return stored;
    const fresh = `sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem('sesam_ai_session', fresh);
    return fresh;
  });
  const scrollRef = useRef(null);

  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  const modulesAllowed = user?.modules_allowed;
  const hasAccess = isAdmin || (Array.isArray(modulesAllowed) && modulesAllowed.includes('ai_assistant'));

  useEffect(() => {
    if (!open || !hasAccess) return;
    axios.get(`${API}/api/ai/chat/history/${sessionId}`)
      .then((r) => setMessages(r.data?.messages || []))
      .catch(() => {});
  }, [open, sessionId, hasAccess]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setSending(true);
    try {
      const r = await axios.post(`${API}/api/ai/chat`, { session_id: sessionId, message: text });
      setMessages(prev => [...prev, { role: 'assistant', content: r.data?.reply || '…' }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Erreur — réessayez';
      setMessages(prev => [...prev, { role: 'assistant', content: `⚠️ ${detail}` }]);
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  const clearChat = async () => {
    if (!window.confirm('Effacer toute la conversation ?')) return;
    try {
      await axios.delete(`${API}/api/ai/chat/history/${sessionId}`);
      setMessages([]);
      toast.success('Conversation effacée');
    } catch {
      toast.error('Impossible d\'effacer');
    }
  };

  if (!hasAccess) return null;

  const suggestions = [
    'Rédige un email de relance pour un devis non signé',
    'Résume mes tâches du jour',
    'Propose une réponse à un lead qui hésite',
    'Modèle SMS de rappel technicien',
  ];

  return (
    <>
      {/* Bouton flottant */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-indigo-600 to-purple-600 text-white shadow-2xl hover:scale-105 transition-transform flex items-center justify-center group"
          data-testid="ai-widget-toggle"
          aria-label="Ouvrir l'assistant IA"
        >
          <Sparkles className="h-6 w-6" />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full ring-2 ring-white animate-pulse" />
          <span className="absolute right-full mr-3 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            Assistant SESAM AI
          </span>
        </button>
      )}

      {/* Panneau chat */}
      {open && (
        <div className="fixed bottom-6 right-6 z-40 w-[92vw] sm:w-[400px] h-[600px] max-h-[85vh] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden" data-testid="ai-widget-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-br from-indigo-600 to-purple-600 text-white">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                <Sparkles className="h-5 w-5" />
              </div>
              <div>
                <div className="font-bold text-sm leading-tight">Assistant SESAM AI</div>
                <div className="text-[10px] opacity-80 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> GPT-5.6 Sol · en ligne
                </div>
              </div>
            </div>
            <div className="flex items-center gap-0.5">
              <button
                onClick={clearChat}
                className="w-8 h-8 rounded-full hover:bg-white/15 flex items-center justify-center"
                title="Nouveau chat"
                data-testid="ai-widget-clear"
              >
                <Trash2 className="h-4 w-4" />
              </button>
              <button
                onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-full hover:bg-white/15 flex items-center justify-center"
                title="Fermer"
                data-testid="ai-widget-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50">
            {messages.length === 0 && (
              <div className="space-y-3">
                <div className="text-center py-4">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 mb-2">
                    <Bot className="h-7 w-7 text-indigo-600" />
                  </div>
                  <p className="text-sm font-semibold text-slate-800">Bonjour {user?.name?.split(' ')[0] || 'Admin'} !</p>
                  <p className="text-xs text-slate-500 mt-1">Comment puis-je vous aider aujourd'hui ?</p>
                </div>
                <div className="space-y-1.5">
                  {suggestions.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => { setInput(s); setTimeout(() => send(), 50); }}
                      className="w-full text-left px-3 py-2 text-xs bg-white border border-slate-200 rounded-lg hover:bg-indigo-50 hover:border-indigo-300 transition"
                      data-testid={`ai-suggestion-${i}`}
                    >
                      💡 {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, idx) => (
              <div key={idx} className={`flex gap-2 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <div className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center ${
                  m.role === 'user' ? 'bg-slate-300' : 'bg-gradient-to-br from-indigo-500 to-purple-500'
                }`}>
                  {m.role === 'user' ? <UserIcon className="h-4 w-4 text-slate-700" /> : <Bot className="h-4 w-4 text-white" />}
                </div>
                <div className={`max-w-[85%] px-3.5 py-2.5 text-sm rounded-2xl ${
                  m.role === 'user'
                    ? 'bg-indigo-600 text-white rounded-tr-md'
                    : 'bg-white text-slate-800 border border-slate-200 rounded-tl-md'
                }`}>
                  {m.role === 'assistant'
                    ? <div className="prose prose-sm max-w-none leading-relaxed" dangerouslySetInnerHTML={{ __html: renderMarkdown(m.content) }} />
                    : <div className="whitespace-pre-wrap">{m.content}</div>
                  }
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex gap-2">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                  <Bot className="h-4 w-4 text-white" />
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-md px-3.5 py-2.5 flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />
                  <span className="text-xs text-slate-500">L'assistant réfléchit…</span>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-slate-200 bg-white p-3">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                placeholder="Écrivez votre message… (Entrée pour envoyer)"
                rows={1}
                className="flex-1 resize-none px-3 py-2 text-sm bg-white text-slate-900 placeholder:text-slate-400 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 max-h-24"
                data-testid="ai-widget-input"
                disabled={sending}
              />
              <Button
                onClick={send}
                disabled={!input.trim() || sending}
                className="h-10 w-10 p-0 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 hover:opacity-90"
                data-testid="ai-widget-send"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
            <div className="text-[10px] text-slate-400 mt-1.5 text-center">
              Propulsé par GPT-5.6 Sol · L'IA peut se tromper, vérifiez les infos importantes
            </div>
          </div>
        </div>
      )}
    </>
  );
}
